import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Organization, FeatureFlags, OrganizationLimits, OrganizationStats } from '../types/api';

interface OrganizationState {
  // State
  currentOrganization: Organization | null;
  features: FeatureFlags | null;
  limits: OrganizationLimits | null;
  stats: OrganizationStats | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  setOrganization: (org: Organization) => void;
  updateFeatures: (features: FeatureFlags) => void;
  updateLimits: (limits: OrganizationLimits) => void;
  updateStats: (stats: OrganizationStats) => void;
  clearOrganization: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

export const useOrganizationStore = create<OrganizationState>()(
  persist(
    (set) => ({
      // Initial state
      currentOrganization: null,
      features: null,
      limits: null,
      stats: null,
      isLoading: false,
      error: null,

      // Actions
      setOrganization: (org: Organization) => {
        set({
          currentOrganization: org,
          features: org.features,
          limits: org.limits,
        });
      },

      updateFeatures: (features: FeatureFlags) => {
        set((state) => ({
          features,
          currentOrganization: state.currentOrganization
            ? { ...state.currentOrganization, features }
            : null,
        }));
      },

      updateLimits: (limits: OrganizationLimits) => {
        set((state) => ({
          limits,
          currentOrganization: state.currentOrganization
            ? { ...state.currentOrganization, limits }
            : null,
        }));
      },

      updateStats: (stats: OrganizationStats) => {
        set({ stats });
      },

      clearOrganization: () => {
        set({
          currentOrganization: null,
          features: null,
          limits: null,
          stats: null,
          error: null,
        });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      setError: (error: string | null) => {
        set({ error });
      },
    }),
    {
      name: 'organization-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        currentOrganization: state.currentOrganization,
        features: state.features,
        limits: state.limits,
      }),
    }
  )
);

// Selector hooks
export const useOrganization = () => useOrganizationStore((state) => state.currentOrganization);
export const useOrganizationFeatures = () => useOrganizationStore((state) => state.features);
export const useOrganizationLimits = () => useOrganizationStore((state) => state.limits);
export const useOrganizationStats = () => useOrganizationStore((state) => state.stats);

// Feature checking hooks
export const useHasFeature = (feature: keyof FeatureFlags) => {
  const features = useOrganizationFeatures();
  return features?.[feature] ?? false;
};

// Limit checking hooks
export const useIsWithinLimit = (resource: 'users' | 'children' | 'storage_gb' | 'file_size_mb', current: number) => {
  const limits = useOrganizationLimits();
  const _stats = useOrganizationStats();
  
  if (!limits) return true; // No limits set
  
  switch (resource) {
    case 'users':
      return current < limits.max_users;
    case 'children':
      return current < limits.max_children;
    case 'storage_gb':
      return current < limits.max_storage_gb;
    case 'file_size_mb':
      return current <= limits.max_file_size_mb;
    default:
      return true;
  }
};

export const useCanUploadFile = (fileSizeMB: number) => {
  const limits = useOrganizationLimits();
  const stats = useOrganizationStats();
  
  if (!limits || !stats) return true;
  
  // Check file size limit
  if (fileSizeMB > limits.max_file_size_mb) {
    return false;
  }
  
  // Check storage limit
  const newStorageGB = stats.storage_used_gb + (fileSizeMB / 1024);
  return newStorageGB <= limits.max_storage_gb;
};
